import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "./Reveal";
import { SocialLinks } from "./SocialLinks";
import { Mail, MapPin, Phone, Send, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";

export function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, lang });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative z-10 py-16 sm:py-24 bg-[#F1EEE8] border-t border-[#0F172A]/5">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="contact.eyebrow" as="span" /></span>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-tight">
            <EditableText k="contact.title" as="span" />
          </h2>
          <p className="mt-6 text-[#475569] leading-relaxed max-w-md">
            <EditableText k="contact.desc" as="span" />
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full bg-[#E8E6E1] flex items-center justify-center text-[#BE923C]">
                <MapPin size={18} />
              </span>
              <span className="text-[#0F172A]"><EditableText k="contact.address" as="span" /></span>
            </div>
            {t("contact.whatsapp").replace(/\D/g, "") && (
              <a
                href={`https://wa.me/${t("contact.whatsapp").replace(/\D/g, "")}`}
                target="_blank" rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-flex items-center gap-2.5 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white font-semibold px-6 py-3 shadow-[0_10px_30px_-8px_rgba(190,146,60,0.5)] hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
              >
                <MessageCircle size={18} /> <EditableText k="contact.whatsapp_cta" as="span" />
              </a>
            )}
          </div>
          <SocialLinks variant="contact" className="mt-8" />

          <div
            data-testid="contact-map"
            className="mt-8 rounded-3xl overflow-hidden border border-[#0F172A]/10 shadow-[0_12px_40px_rgba(15,23,42,0.08)]"
          >
            <iframe
              title="U Milosrdných 6, Praha 1"
              src="https://maps.google.com/maps?q=U%20Milosrdn%C3%BDch%206%2C%20Praha%201&z=16&output=embed"
              className="w-full h-56 sm:h-64 border-0 block"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>

        <Reveal delay={0.1} className="h-full flex flex-col justify-end">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl bg-white border border-[#0F172A]/5 shadow-[0_20px_60px_rgba(15,23,42,0.06)] p-7 sm:p-9 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="c-name"><EditableText k="contact.name" as="span" /></Label>
                <Input id="c-name" data-testid="contact-name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 border-[#0F172A]/10" />
              </div>
              <div>
                <Label htmlFor="c-email"><EditableText k="contact.email" as="span" /></Label>
                <Input id="c-email" data-testid="contact-email" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 border-[#0F172A]/10" />
              </div>
            </div>
            <div>
              <Label htmlFor="c-subject"><EditableText k="contact.subject" as="span" /></Label>
              <Input id="c-subject" data-testid="contact-subject" value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5 border-[#0F172A]/10" />
            </div>
            <div>
              <Label htmlFor="c-message"><EditableText k="contact.message" as="span" /></Label>
              <Textarea id="c-message" data-testid="contact-message" required rows={5} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 border-[#0F172A]/10 resize-none" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] text-white font-semibold py-3.5 hover:bg-[#1e293b] transition-colors disabled:opacity-60"
            >
              <Send size={16} /> {loading ? t("contact.sending") : t("contact.send")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
